import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { GitBranch } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Assignment = () => {
  const [dispatches, setDispatches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDispatch, setSelectedDispatch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedTruck, setSelectedTruck] = useState('');
  const [selectedTrailer, setSelectedTrailer] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');

  useEffect(() => {
    fetchDispatches();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyAssets(selectedCompany);
    } else {
      setTrucks([]);
      setTrailers([]);
      setDrivers([]);
    }
  }, [selectedCompany]);

  const fetchDispatches = async () => {
    try {
      const response = await axios.get(`${API}/dispatches?status=En Proceso`);
      setDispatches(response.data);
    } catch (error) {
      toast.error('Error al cargar despachos');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    }
  };

  const fetchCompanyAssets = async (companyId) => {
    try {
      const [trucksRes, trailersRes, driversRes] = await Promise.all([
        axios.get(`${API}/trucks?company_id=${companyId}`),
        axios.get(`${API}/trailers?company_id=${companyId}`),
        axios.get(`${API}/drivers?company_id=${companyId}`)
      ]);
      setTrucks(trucksRes.data);
      setTrailers(trailersRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error('Error al cargar recursos de la empresa');
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedDispatch || !selectedCompany || !selectedTruck || !selectedTrailer || !selectedDriver) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    try {
      await axios.post(`${API}/dispatches/assign`, {
        dispatch_id: selectedDispatch,
        company_id: selectedCompany,
        truck_id: selectedTruck,
        trailer_id: selectedTrailer,
        driver_id: selectedDriver
      });
      toast.success('Equipo asignado exitosamente');
      resetForm();
      fetchDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar equipo');
    }
  };

  const resetForm = () => {
    setSelectedDispatch('');
    setSelectedCompany('');
    setSelectedTruck('');
    setSelectedTrailer('');
    setSelectedDriver('');
    setTrucks([]);
    setTrailers([]);
    setDrivers([]);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="assignment-page">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900 tracking-tight">Asignación de Equipos</h1>
          <p className="text-slate-600 mt-1">Asigna equipos a despachos en proceso</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <form onSubmit={handleAssign} className="space-y-6">
            <div className="space-y-2">
              <Label>Seleccionar Despacho (En Proceso)</Label>
              <Select value={selectedDispatch} onValueChange={setSelectedDispatch} data-testid="assign-dispatch-select">
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar despacho" />
                </SelectTrigger>
                <SelectContent>
                  {dispatches.map(dispatch => (
                    <SelectItem key={dispatch.id} value={dispatch.id}>
                      {dispatch.dispatch_number} - {dispatch.destination}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Seleccionar Empresa</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany} data-testid="assign-company-select">
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedCompany && (
              <>
                <div className="space-y-2">
                  <Label>Seleccionar Camión</Label>
                  <Select value={selectedTruck} onValueChange={setSelectedTruck} data-testid="assign-truck-select">
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar camión" />
                    </SelectTrigger>
                    <SelectContent>
                      {trucks.map(truck => (
                        <SelectItem key={truck.id} value={truck.id}>
                          {truck.license_plate} - {truck.brand} {truck.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Seleccionar Acoplado</Label>
                  <Select value={selectedTrailer} onValueChange={setSelectedTrailer} data-testid="assign-trailer-select">
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar acoplado" />
                    </SelectTrigger>
                    <SelectContent>
                      {trailers.map(trailer => (
                        <SelectItem key={trailer.id} value={trailer.id}>
                          {trailer.license_plate} - {trailer.brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Seleccionar Chofer</Label>
                  <Select value={selectedDriver} onValueChange={setSelectedDriver} data-testid="assign-driver-select">
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar chofer" />
                    </SelectTrigger>
                    <SelectContent>
                      {drivers.map(driver => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name} - DNI: {driver.dni}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <Button 
              type="submit" 
              data-testid="submit-assignment" 
              className="w-full bg-primary hover:bg-primary-hover text-white"
              disabled={!selectedDispatch || !selectedCompany || !selectedTruck || !selectedTrailer || !selectedDriver}
            >
              <GitBranch className="mr-2" size={18} />
              Asignar Equipo al Despacho
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Assignment;
